import { Link } from "react-router-dom";
import { CONTACT_EMAIL, SITE_ORIGIN } from "../config/site";
import { BtnContent } from "../components/BtnIcon";
import { IoStorefrontOutline } from "react-icons/io5";

function Privacy() {
  const lastUpdated = "July 7, 2026";

  return (
    <article className="container py-4 py-md-5" style={{ maxWidth: "720px" }}>
      <header className="mb-4">
        <h1 className="h3 mb-2">Privacy policy</h1>
        <p className="text-muted small mb-0">Last updated: {lastUpdated}</p>
      </header>

      <p className="small mb-4">
        Rencin Gifting (&ldquo;we&rdquo;, &ldquo;us&rdquo;, or &ldquo;our&rdquo;) operates{" "}
        <a href={SITE_ORIGIN}>{SITE_ORIGIN.replace(/^https?:\/\//, "")}</a> and sells curated gift
        boxes and hampers across India. This policy explains how we collect, use, and protect your
        personal information when you browse our site, place an order, or contact us.
      </p>

      <section className="mb-4">
        <h2 className="h5 mb-2">Information we collect</h2>
        <p className="small mb-2">We may collect the following information:</p>
        <ul className="small ps-3 mb-0">
          <li className="mb-1">
            <strong>Contact details</strong> — name, email address, and phone number when you
            checkout, sign in, or send us a message.
          </li>
          <li className="mb-1">
            <strong>Delivery details</strong> — shipping address, city, state, and PIN code needed
            to fulfil your order.
          </li>
          <li className="mb-1">
            <strong>Order information</strong> — products purchased, customisation notes, order
            history, and payment status.
          </li>
          <li className="mb-1">
            <strong>Account data</strong> — if you sign in with email OTP, we store a session token
            in your browser to keep you logged in.
          </li>
          <li>
            <strong>Technical data</strong> — basic usage information such as browser type, device,
            and pages visited, which may be collected through standard server logs or analytics
            tools.
          </li>
        </ul>
      </section>

      <section className="mb-4">
        <h2 className="h5 mb-2">How we use your information</h2>
        <p className="small mb-2">We use your information to:</p>
        <ul className="small ps-3 mb-0">
          <li className="mb-1">Process and deliver your orders.</li>
          <li className="mb-1">Confirm payments and send order updates.</li>
          <li className="mb-1">Respond to enquiries and provide customer support.</li>
          <li className="mb-1">Manage loyalty offers where applicable.</li>
          <li className="mb-1">Improve our website, products, and shopping experience.</li>
          <li>Comply with legal, tax, and regulatory obligations.</li>
        </ul>
      </section>

      <section className="mb-4">
        <h2 className="h5 mb-2">Cookies and local storage</h2>
        <p className="small mb-0">
          We use your browser&apos;s local storage to remember your shopping cart and, if you sign
          in, your customer session. These are stored on your device and are not used for
          third-party advertising. You can clear them at any time through your browser settings,
          though this may remove items from your cart or sign you out.
        </p>
      </section>

      <section className="mb-4">
        <h2 className="h5 mb-2">Sharing with third parties</h2>
        <p className="small mb-2">
          We do not sell your personal information. We share data only when needed to run our
          business:
        </p>
        <ul className="small ps-3 mb-0">
          <li className="mb-1">
            <strong>Payment partners</strong> — such as SMEPay, to process UPI and other payments
            securely.
          </li>
          <li className="mb-1">
            <strong>Shipping partners</strong> — such as Shiprocket and courier services, to deliver
            your order to the address you provide.
          </li>
          <li className="mb-1">
            <strong>Hosting and infrastructure</strong> — to operate our website and store order
            records securely.
          </li>
          <li>
            <strong>Legal requirements</strong> — when required by law, court order, or to protect
            our rights and customers.
          </li>
        </ul>
      </section>

      <section className="mb-4">
        <h2 className="h5 mb-2">Data retention</h2>
        <p className="small mb-0">
          We keep order and contact records for as long as needed to fulfil your purchase, provide
          support, and meet accounting or legal requirements. You may ask us to delete information
          that is no longer required, subject to any records we must retain by law.
        </p>
      </section>

      <section className="mb-4">
        <h2 className="h5 mb-2">Your rights</h2>
        <p className="small mb-2">Depending on applicable law, you may have the right to:</p>
        <ul className="small ps-3 mb-0">
          <li className="mb-1">Access the personal information we hold about you.</li>
          <li className="mb-1">Request correction of inaccurate details.</li>
          <li className="mb-1">Request deletion of your data, where permitted.</li>
          <li>Withdraw consent for marketing communications at any time.</li>
        </ul>
      </section>

      <section className="mb-4">
        <h2 className="h5 mb-2">Security</h2>
        <p className="small mb-0">
          We take reasonable technical and organisational measures to protect your information.
          Payment processing is handled by certified payment providers. No method of transmission
          over the internet is completely secure, and we cannot guarantee absolute security.
        </p>
      </section>

      <section className="mb-4">
        <h2 className="h5 mb-2">Children</h2>
        <p className="small mb-0">
          Our services are not directed at children under 18. We do not knowingly collect personal
          information from children. If you believe a child has provided us with personal data,
          please contact us and we will delete it.
        </p>
      </section>

      <section className="mb-4">
        <h2 className="h5 mb-2">Changes to this policy</h2>
        <p className="small mb-0">
          We may update this privacy policy from time to time. The &ldquo;Last updated&rdquo; date
          at the top of this page will reflect any changes. Continued use of the site after updates
          means you accept the revised policy.
        </p>
      </section>

      <section className="mb-4">
        <h2 className="h5 mb-2">Contact us</h2>
        <p className="small mb-0">
          For privacy-related questions or requests, email us at{" "}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> or visit our{" "}
          <Link to="/about">contact page</Link>.
        </p>
      </section>

      <p className="mb-0">
        <Link to="/shop" className="btn btn-primary btn-sm">
          <BtnContent icon={IoStorefrontOutline}>Back to shop</BtnContent>
        </Link>
      </p>
    </article>
  );
}

export default Privacy;
